import { FilterDropdownProps } from "antd/es/table/interface";
import DropdownSearchFilter from "@lib/components/MiniComponents/TableFilters/DropdownSearchFilter";
import { useMemo } from "react";
import { StyledFlex } from "../Table/index.styled";
import { green, red } from "@ant-design/colors";
import { AppTag } from "@lib/components/AppComponents";
import { LANGUAGE_CODE } from "@lib/types/Country";
import { convert2label } from "@lib/helpers/StringHelper";

type LanguageFiltersProps = FilterDropdownProps & {
  dataIndex: string;
  apiData?: any[];
};

const LanguageFilters: React.FC<LanguageFiltersProps> = ({
  confirm,
  setSelectedKeys,
  selectedKeys,
  dataIndex,
  apiData = [],
}) => {
  // Group apiData by language and count occurrences
  const groupedCounts = useMemo(() => {
    if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
      return {};
    }

    const counts: Record<string, number> = {};

    apiData.forEach((item) => {
      const languageValue = item[dataIndex];
      if (languageValue !== undefined && languageValue !== null) {
        const key = String(languageValue);
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [apiData, dataIndex]);

  const filterData = useMemo(() => {
    return Object.entries(LANGUAGE_CODE)
      .map(([key, value]) => {
        const count = groupedCounts[value] || 0;
        const label = convert2label(key);
        return {
          text: (
            <StyledFlex
              style={{ gap: 4, justifyContent: "space-between", width: "100%" }}
            >
              <span>{label}</span>
              <AppTag
                color={count > 0 ? green[7] : red[7]}
                style={{ margin: 0 }}
              >
                {count || 0}
              </AppTag>
            </StyledFlex>
          ),
          label: label,
          value: value,
          count: count,
        };
      })
      .sort((a, b) => {
        // First sort by count (count > 0 first)
        if (a.count > 0 && b.count === 0) return -1;
        if (a.count === 0 && b.count > 0) return 1;
        // Then sort alphabetically by label
        return a.label.localeCompare(b.label);
      });
  }, [groupedCounts]);

  const currentValue = useMemo(() => {
    if (!selectedKeys || selectedKeys.length === 0) return undefined;
    return Array.isArray(selectedKeys)
      ? selectedKeys.map((key) => String(key))
      : String(selectedKeys).split(",").filter(Boolean);
  }, [selectedKeys]);

  return (
    <DropdownSearchFilter
      field={dataIndex}
      data={filterData}
      value={currentValue}
      isLoading={false}
      enableSearch={true}
      onFinish={(e) => {
        if (setSelectedKeys) {
          setSelectedKeys(e.value ? (e.value as any) : []);
        }
        if (confirm) {
          confirm();
        }
      }}
    />
  );
};

export default LanguageFilters;
